import type React from 'react';
import { useTenderDetails } from '../../contexts/TenderDetailsContext';
import AdHocDocGenerationSection from './AdHocDocGenerationSection';
import WorkflowStep from './WorkflowStep';

const AdHocDocGenerationStep: React.FC = () => {
  const {
    canManageTender,
    openStep,
    setOpenStep,
    tenderForms,
    stepCompletion,
    toggleStepCompletion,
    validTenderId,
    tender,
    loadingStates,
    isDraggingForm,
    handleDragEventsForm,
    handleDropForm,
    handleFileUpload,
    uploadValidationWarning,
    processingFiles,
    handleGenerateAdHocDocument,
    handleDownloadFormInput,
    handleDownloadFilledForm,
    handleDeleteTenderForm,
    handleResetForm,
    sectionsCount,
    handleResetAdHocService,
  } = useTenderDetails();

  if (!canManageTender) {
    return null;
  }

  const sortedTenderForms = [...tenderForms].sort((a, b) => {
    const timeA = a.uploadedAt ? new Date(a.uploadedAt as string | number | Date).getTime() : 0;
    const timeB = b.uploadedAt ? new Date(b.uploadedAt as string | number | Date).getTime() : 0;
    if (timeB !== timeA) {
      return timeB - timeA;
    }
    // Fallback to alphabetical sort by filename for absolute stability when timestamps match
    const nameA = (a.originalFilename || a.name || '').toLowerCase();
    const nameB = (b.originalFilename || b.name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  return (
    <WorkflowStep
      stepNumber={12}
      expanded={openStep === 12}
      onChange={(_, isExpanded) => setOpenStep(isExpanded ? 12 : null)}
      title="Fill Forms and Generate Documents"
      description="Fill returnable forms and generate additional tender-aligned spreadsheets, addenda, etc."
      isComplete={
        // biome-ignore lint/suspicious/noExplicitAny: Required for arbitrary tender form objects
        sortedTenderForms.some((f: any) => f.generationStatus === 'success') || !!stepCompletion[12]
      }
      onToggleComplete={() => toggleStepCompletion(12)}
      optional={true}
    >
      <AdHocDocGenerationSection
        tenderId={validTenderId}
        usedCount={tender.adhocDocCount || 0}
        tenderForms={sortedTenderForms}
        loadingStates={loadingStates}
        isDragging={isDraggingForm}
        onDragEnter={handleDragEventsForm}
        onDragOver={handleDragEventsForm}
        onDragLeave={handleDragEventsForm}
        onDrop={handleDropForm}
        onFileUpload={(file, isStrictForm) =>
          handleFileUpload(file, 'tender_form', {
            ...(isStrictForm !== undefined && { isStrictForm }),
          })
        }
        uploadValidationWarning={uploadValidationWarning.form}
        processingFiles={processingFiles.form}
        onGenerateForm={handleGenerateAdHocDocument}
        onDownloadInput={handleDownloadFormInput}
        onDownloadFilledForm={handleDownloadFilledForm}
        onDeleteForm={handleDeleteTenderForm}
        onResetForm={handleResetForm}
        isDraftGenerated={!!tender.draftFileLink || sectionsCount > 0}
        onResetService={() => handleResetAdHocService(tenderForms)}
      />
    </WorkflowStep>
  );
};

export default AdHocDocGenerationStep;
